import { useState, useEffect } from "react";
import { toast } from "sonner";
import { getChecksEmpresa } from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  HelpCircle,
  RefreshCw,
  Database,
  Calculator,
  FileCheck,
  Info,
  ChevronDown,
} from "lucide-react";

const estadoConfig = {
  ok: { color: "bg-emerald-500", icon: CheckCircle2, label: "OK", textColor: "text-emerald-600" },
  warning: { color: "bg-amber-500", icon: AlertTriangle, label: "Aviso", textColor: "text-amber-600" },
  error: { color: "bg-red-500", icon: XCircle, label: "Error", textColor: "text-red-600" },
  na: { color: "bg-gray-400", icon: HelpCircle, label: "N/A", textColor: "text-gray-500" },
  pendiente: { color: "bg-gray-400", icon: HelpCircle, label: "Pendiente", textColor: "text-gray-500" },
};

const categoriaConfig = {
  datos: { icon: Database, label: "Datos", color: "text-blue-500" },
  contable: { icon: Calculator, label: "Contable", color: "text-purple-500" },
};

function CheckItem({ check }) {
  const config = estadoConfig[check.estado] || estadoConfig.na;
  const Icon = config.icon;
  const CategoriaIcon = categoriaConfig[check.categoria]?.icon || FileCheck;
  
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
      <div className={`p-2 rounded-full ${check.estado === 'ok' ? 'bg-emerald-100' : check.estado === 'error' ? 'bg-red-100' : check.estado === 'warning' ? 'bg-amber-100' : 'bg-gray-100'}`}>
        <Icon className={`h-4 w-4 ${config.textColor}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm">{check.nombre}</span>
          <Badge variant="outline" className="text-xs">
            <CategoriaIcon className={`h-3 w-3 mr-1 ${categoriaConfig[check.categoria]?.color}`} />
            {categoriaConfig[check.categoria]?.label || check.categoria}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">{check.descripcion}</p>
        <div className={`text-sm font-medium mt-1 ${config.textColor}`}>
          {check.mensaje}
        </div>
        {check.detalle && Object.keys(check.detalle).length > 0 && (
          <div className="mt-2 text-xs text-muted-foreground bg-background/50 rounded p-2 space-y-0.5">
            {Object.entries(check.detalle).map(([key, value]) => {
              // Para el check de integridad de cuentas, mostrar sin decimales
              const esContador = check.id === 'integridad_cuentas' || check.id === 'cuentas_pendientes' || check.id === 'cuentas_error';
              const formatoNumero = esContador 
                ? { minimumFractionDigits: 0, maximumFractionDigits: 0 }
                : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
              return (
                <div key={key} className="flex justify-between">
                  <span className="capitalize">{key.replace(/_/g, ' ')}:</span>
                  <span className="font-mono">{typeof value === 'number' ? value.toLocaleString('es-ES', formatoNumero) : value}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function ResumenCard({ resumen }) {
  const total = resumen.total;
  const porcentajeOk = resumen.porcentaje_ok;
  
  return (
    <Card className="border-l-4 border-l-primary">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2">
          <FileCheck className="h-5 w-5 text-primary" />
          Resumen de Validaciones
        </CardTitle>
        <CardDescription>Estado general de todos los checks de la empresa</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold">{total}</div>
            <div className="text-xs text-muted-foreground">Total</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-emerald-600">{resumen.ok}</div>
            <div className="text-xs text-muted-foreground flex items-center justify-center gap-1">
              <CheckCircle2 className="h-3 w-3" /> OK
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-amber-600">{resumen.warning}</div>
            <div className="text-xs text-muted-foreground flex items-center justify-center gap-1">
              <AlertTriangle className="h-3 w-3" /> Avisos
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">{resumen.error}</div>
            <div className="text-xs text-muted-foreground flex items-center justify-center gap-1">
              <XCircle className="h-3 w-3" /> Errores
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-500">{resumen.na}</div>
            <div className="text-xs text-muted-foreground flex items-center justify-center gap-1">
              <HelpCircle className="h-3 w-3" /> N/A
            </div>
          </div>
        </div>
        
        {/* Barra de progreso */}
        <div className="mt-4">
          <div className="flex justify-between text-xs text-muted-foreground mb-1">
            <span>Progreso de validación</span>
            <span>{porcentajeOk}% correcto</span>
          </div>
          <div className="h-2 rounded-full bg-muted overflow-hidden flex">
            {resumen.ok > 0 && (
              <div 
                className="h-full bg-emerald-500 transition-all" 
                style={{ width: `${(resumen.ok / total) * 100}%` }}
              />
            )}
            {resumen.warning > 0 && (
              <div 
                className="h-full bg-amber-500 transition-all" 
                style={{ width: `${(resumen.warning / total) * 100}%` }}
              />
            )}
            {resumen.error > 0 && (
              <div 
                className="h-full bg-red-500 transition-all" 
                style={{ width: `${(resumen.error / total) * 100}%` }}
              />
            )}
            {resumen.na > 0 && (
              <div 
                className="h-full bg-gray-300 transition-all" 
                style={{ width: `${(resumen.na / total) * 100}%` }}
              />
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function EjercicioChecks({ ejercicio }) {
  const checksOk = ejercicio.checks.filter(c => c.estado === "ok").length;
  const checksWarning = ejercicio.checks.filter(c => c.estado === "warning").length;
  const checksError = ejercicio.checks.filter(c => c.estado === "error").length;
  const checksNa = ejercicio.checks.filter(c => c.estado === "na" || c.estado === "pendiente").length;
  const total = ejercicio.checks.length;
  
  // Determinar estado general del ejercicio
  let estadoGeneral = "ok";
  if (checksError > 0) estadoGeneral = "error";
  else if (checksWarning > 0) estadoGeneral = "warning";
  else if (checksNa === total) estadoGeneral = "na";
  
  const config = estadoConfig[estadoGeneral];
  const Icon = config.icon;
  
  return (
    <AccordionItem value={ejercicio.ejercicio_id} className="border rounded-lg mb-2 overflow-hidden">
      <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-muted/30 [&[data-state=open]]:bg-muted/30">
        <div className="flex items-center gap-3 w-full">
          <div className={`p-1.5 rounded-full ${estadoGeneral === 'ok' ? 'bg-emerald-100' : estadoGeneral === 'error' ? 'bg-red-100' : estadoGeneral === 'warning' ? 'bg-amber-100' : 'bg-gray-100'}`}>
            <Icon className={`h-4 w-4 ${config.textColor}`} />
          </div>
          <div className="flex-1 text-left">
            <div className="font-semibold">Ejercicio {ejercicio.anio}</div>
            <div className="text-xs text-muted-foreground">
              {checksOk} OK • {checksWarning} avisos • {checksError} errores • {checksNa} N/A
            </div>
          </div>
          <div className="flex gap-1 mr-2">
            {checksOk > 0 && <Badge className="bg-emerald-500 hover:bg-emerald-600 text-xs">{checksOk}</Badge>}
            {checksWarning > 0 && <Badge className="bg-amber-500 hover:bg-amber-600 text-xs">{checksWarning}</Badge>}
            {checksError > 0 && <Badge className="bg-red-500 hover:bg-red-600 text-xs">{checksError}</Badge>}
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-4">
        <div className="space-y-2 pt-2">
          {ejercicio.checks.map((check) => (
            <CheckItem key={check.id} check={check} />
          ))}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}

export default function ChecksValidacionContent({ empresaId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadChecks();
  }, [empresaId]);

  const loadChecks = async () => {
    try {
      setLoading(true);
      const response = await getChecksEmpresa(empresaId);
      setData(response.data);
    } catch (error) {
      toast.error("Error al cargar los checks de validación");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const response = await getChecksEmpresa(empresaId);
      setData(response.data);
      toast.success("Checks actualizados");
    } catch (error) {
      toast.error("Error al actualizar los checks");
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <XCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>No se pudieron cargar los checks</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="checks-validacion-content">
      {/* Header con botón de refrescar */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="section-title">Checks de Validación</h2>
          <p className="text-sm text-muted-foreground">
            Verificaciones de integridad y validaciones contables
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      {/* Resumen global */}
      <ResumenCard resumen={data.resumen} />

      {/* Info sobre los checks */}
      <Card className="bg-blue-50/50 border-blue-200">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-500 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-blue-900 mb-1">¿Qué son los checks?</p>
              <p className="text-blue-700">
                Los checks son validaciones automáticas que verifican la integridad de los datos importados 
                y el cumplimiento de las reglas contables básicas (cuadre del balance, conciliación del flujo de caja, etc.).
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Leyenda */}
      <div className="flex flex-wrap gap-4 text-sm">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          <span className="text-muted-foreground">OK: Check superado</span>
        </div>
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <span className="text-muted-foreground">Aviso: Requiere atención</span>
        </div>
        <div className="flex items-center gap-2">
          <XCircle className="h-4 w-4 text-red-500" />
          <span className="text-muted-foreground">Error: Problema detectado</span>
        </div>
        <div className="flex items-center gap-2">
          <HelpCircle className="h-4 w-4 text-gray-400" />
          <span className="text-muted-foreground">N/A: No aplica</span>
        </div>
      </div>

      <Separator />

      {/* Checks por ejercicio */}
      <div>
        <h3 className="font-semibold mb-3">Detalle por ejercicio</h3>
        {data.ejercicios.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Database className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No hay ejercicios creados</p>
          </div>
        ) : (
          <Accordion type="multiple" defaultValue={data.ejercicios.slice(0, 2).map(e => e.ejercicio_id)}>
            {data.ejercicios.map((ejercicio) => (
              <EjercicioChecks key={ejercicio.ejercicio_id} ejercicio={ejercicio} />
            ))}
          </Accordion>
        )}
      </div>
    </div>
  );
}
